import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CalendarClock, Wand2 } from "lucide-react";

/**
 * Parses an AI reply and renders lead-reference tokens as clickable cards.
 * Token format (produced by the manager system prompt):
 *   [[LEAD:<lead_id>|<name>|<reason>]]
 * Everything else renders as plain text with preserved newlines.
 *
 * onOpenLead(leadId) is called when the user taps the primary [Open Lead] cta.
 */
const TOKEN = /\[\[LEAD:([^|\]]+)\|([^|\]]+)\|([^\]]*)\]\]/g;

export function LeadReferenceRenderer({ text, isUser, onOpenLead }) {
  if (!text) return null;
  // For user messages we never parse tokens.
  if (isUser) return <div className="whitespace-pre-wrap">{text}</div>;

  const nodes = [];
  let last = 0;
  let match;
  let key = 0;
  TOKEN.lastIndex = 0;
  while ((match = TOKEN.exec(text)) !== null) {
    const [full, leadId, name, reason] = match;
    if (match.index > last) {
      nodes.push(<span key={`t-${key++}`}>{text.slice(last, match.index)}</span>);
    }
    nodes.push(
      <LeadCard key={`c-${key++}`} leadId={leadId.trim()} name={name.trim()} reason={reason.trim()} onOpenLead={onOpenLead} />
    );
    last = match.index + full.length;
  }
  if (last < text.length) nodes.push(<span key={`t-${key++}`}>{text.slice(last)}</span>);
  return <div className="whitespace-pre-wrap">{nodes}</div>;
}

function LeadCard({ leadId, name, reason, onOpenLead }) {
  const nav = useNavigate();
  const openLead = () => {
    if (onOpenLead) return onOpenLead(leadId);
    const base = window.location.pathname.startsWith("/admin") ? "/admin" : "/app";
    nav(`${base}/leads/${leadId}`);
  };
  return (
    <div
      data-testid={`ai-lead-card-${leadId}`}
      className="my-2 flex flex-col gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-foreground shadow-sm"
    >
      <div>
        <div className="font-semibold leading-tight">{name}</div>
        {reason && <div className="mt-0.5 text-xs text-muted-foreground">{reason}</div>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={openLead}
          data-testid={`ai-open-lead-${leadId}`}
          className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-[11px] font-semibold text-primary-foreground tap-lift"
        >
          Open Lead <ArrowRight className="h-3 w-3" />
        </button>
        <button
          onClick={openLead}
          data-testid={`ai-set-followup-${leadId}`}
          title="Opens the lead where you can add a follow-up"
          className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-3 py-1 text-[11px] font-semibold text-muted-foreground tap-lift hover:text-foreground"
        >
          <CalendarClock className="h-3 w-3" /> Set Follow-up
        </button>
        <button
          onClick={openLead}
          data-testid={`ai-update-status-${leadId}`}
          title="Opens the lead where you can update its status"
          className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-3 py-1 text-[11px] font-semibold text-muted-foreground tap-lift hover:text-foreground"
        >
          <Wand2 className="h-3 w-3" /> Update Status
        </button>
      </div>
    </div>
  );
}
